var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Menu, MenuItem, Checkbox, ListItemText, TextField, Button, Typography, } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { makeStyles } from 'tss-react/mui';
import { useAppSelector } from 'hooks/reduxHooks';
import { Roles } from 'globals/enums';
var BPO_ALLOWED_CALL_TYPES = ['All', 'Fall Detected', 'SOS'];
var FACILITY_AGENT_CALL_TYPES = [
    'All',
    'SOS',
    'Fall Detected',
    'Emergency Room Visit',
    'Hospitalization',
];
var CALL_TYPE_OPTIONS = [
    'All',
    'Senior Onboarding',
    'Caregiver Onboarding',
    'Senior Check-in',
    'Caregiver Check-in',
    'Return Call',
    'Agency follow-up',
    'Fall Detected',
    'SOS',
    'Care Coordination',
    'Care Insight Notification',
    'Hospitalization',
    'Emergency Room Visit',
    'Other',
];
var CALL_DIRECTION_OPTIONS = ['All', 'Inbound', 'Outbound'];
var CONTROL_HEIGHT = 40;
var useStyles = makeStyles()(function () { return ({
    paper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '14px 20px',
        marginBottom: 16,
        borderRadius: 10,
        border: '1px solid #E0E5E9',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.06)',
    },
    controlsRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 10,
        width: '100%',
        minWidth: 0,
    },
    actionsRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        width: '100%',
    },
    divider: {
        width: 1,
        height: 32,
        backgroundColor: '#E0E5E9',
        flexShrink: 0,
    },
    dateGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 1,
        minWidth: 0,
    },
    dateField: {
        flex: '1 1 120px',
        minWidth: 120,
        maxWidth: 165,
        '& .MuiOutlinedInput-root': {
            height: CONTROL_HEIGHT,
            borderRadius: 8,
            backgroundColor: '#F8FAF8',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5DBD5',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#32602F',
        },
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#32602F',
        },
        '& input': {
            padding: '8.5px 10px',
            cursor: 'pointer',
        },
    },
    clearButton: {
        height: CONTROL_HEIGHT,
        color: '#32602F',
        borderColor: '#32602F',
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': {
            borderColor: '#244022',
            backgroundColor: 'rgba(50, 96, 47, 0.04)',
        },
    },
    applyButton: {
        height: CONTROL_HEIGHT,
        backgroundColor: '#32602F',
        color: '#FFFFFF',
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': {
            backgroundColor: '#244022',
            boxShadow: 'none',
        },
    },
    menuItem: {
        minWidth: 230,
        justifyContent: 'space-between',
        fontWeight: 500,
    },
    menuOptions: {
        paddingTop: 4,
        paddingBottom: 4,
        backgroundColor: '#F8FAF6',
        borderLeft: '2px solid #6BA539',
        marginLeft: 16,
    },
    errorText: {
        color: '#d32f2f',
        fontSize: 12,
        marginTop: -4,
        whiteSpace: 'nowrap',
    },
}); });
var DEFAULT_FILTER_VALUE = {
    callType: [],
    callDirection: 'All',
    startDate: '',
    endDate: '',
};
var SeniorCallLogsFilter = function (_a) {
    var _b, _c, _d, _e;
    var disabled = _a.disabled, initialValue = _a.initialValue, onChange = _a.onChange, _f = _a.careAgent, careAgent = _f === void 0 ? [] : _f, _g = _a.careAgentOptions, careAgentOptions = _g === void 0 ? ['All'] : _g, onCareAgentChange = _a.onCareAgentChange;
    var classes = useStyles().classes;
    var userRole = useAppSelector(function (state) { return state.auth.roleConfig.role; });
    var callTypeOptions = React.useMemo(function () {
        var roles = Array.isArray(userRole) ? userRole : [userRole];
        if (roles.includes(Roles.FACILITY_AGENT)) {
            return CALL_TYPE_OPTIONS.filter(function (option) {
                return FACILITY_AGENT_CALL_TYPES.includes(option);
            });
        }
        if (roles.includes(Roles.BPO_CareUser)) {
            return CALL_TYPE_OPTIONS.filter(function (option) {
                return BPO_ALLOWED_CALL_TYPES.includes(option);
            });
        }
        return CALL_TYPE_OPTIONS;
    }, [userRole]);
    var _h = useState((_b = initialValue === null || initialValue === void 0 ? void 0 : initialValue.callType) !== null && _b !== void 0 ? _b : DEFAULT_FILTER_VALUE.callType), callType = _h[0], setCallType = _h[1];
    var _j = useState(false), isCallTypeAllDeselected = _j[0], setIsCallTypeAllDeselected = _j[1];
    var _k = useState((_c = initialValue === null || initialValue === void 0 ? void 0 : initialValue.callDirection) !== null && _c !== void 0 ? _c : DEFAULT_FILTER_VALUE.callDirection), callDirection = _k[0], setCallDirection = _k[1];
    var _l = useState(false), isCallDirectionAllDeselected = _l[0], setIsCallDirectionAllDeselected = _l[1];
    var _m = useState((_d = initialValue === null || initialValue === void 0 ? void 0 : initialValue.startDate) !== null && _d !== void 0 ? _d : DEFAULT_FILTER_VALUE.startDate), appliedStartDate = _m[0], setAppliedStartDate = _m[1];
    var _o = useState((_e = initialValue === null || initialValue === void 0 ? void 0 : initialValue.endDate) !== null && _e !== void 0 ? _e : DEFAULT_FILTER_VALUE.endDate), appliedEndDate = _o[0], setAppliedEndDate = _o[1];
    var _p = useState(appliedStartDate), startDate = _p[0], setStartDate = _p[1];
    var _q = useState(appliedEndDate), endDate = _q[0], setEndDate = _q[1];
    var _r = useState(careAgent), selectedCareAgents = _r[0], setSelectedCareAgents = _r[1];
    var _s = useState(''), careAgentSearch = _s[0], setCareAgentSearch = _s[1];
    var _t = useState(false), isCareAgentAllDeselected = _t[0], setIsCareAgentAllDeselected = _t[1];
    var _u = useState(null), filterAnchor = _u[0], setFilterAnchor = _u[1];
    var _v = useState(null), activeFilter = _v[0], setActiveFilter = _v[1];
    var previousInitialValue = useRef(initialValue);
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!initialValue)
            return;
        var nextCallType = (_a = initialValue.callType) !== null && _a !== void 0 ? _a : DEFAULT_FILTER_VALUE.callType;
        var previousValue = previousInitialValue.current;
        if (JSON.stringify(nextCallType) !==
            JSON.stringify((_b = previousValue === null || previousValue === void 0 ? void 0 : previousValue.callType) !== null && _b !== void 0 ? _b : DEFAULT_FILTER_VALUE.callType)) {
            setCallType(nextCallType);
            setIsCallTypeAllDeselected(false);
        }
        var nextCallDirection = (_c = initialValue.callDirection) !== null && _c !== void 0 ? _c : DEFAULT_FILTER_VALUE.callDirection;
        if (nextCallDirection !==
            ((_d = previousValue === null || previousValue === void 0 ? void 0 : previousValue.callDirection) !== null && _d !== void 0 ? _d : DEFAULT_FILTER_VALUE.callDirection)) {
            setCallDirection(nextCallDirection);
            setIsCallDirectionAllDeselected(false);
        }
        setAppliedStartDate((_e = initialValue.startDate) !== null && _e !== void 0 ? _e : DEFAULT_FILTER_VALUE.startDate);
        setAppliedEndDate((_f = initialValue.endDate) !== null && _f !== void 0 ? _f : DEFAULT_FILTER_VALUE.endDate);
        setStartDate((_g = initialValue.startDate) !== null && _g !== void 0 ? _g : DEFAULT_FILTER_VALUE.startDate);
        setEndDate((_h = initialValue.endDate) !== null && _h !== void 0 ? _h : DEFAULT_FILTER_VALUE.endDate);
        previousInitialValue.current = initialValue;
    }, [initialValue]);
    var dateRangeInvalid = !!startDate && !!endDate && new Date(startDate) > new Date(endDate);
    var hasPendingDateChange = startDate !== appliedStartDate || endDate !== appliedEndDate;
    var hasAnyDateValue = !!startDate || !!endDate || !!appliedStartDate || !!appliedEndDate;
    var callTypeValues = callTypeOptions.filter(function (option) { return option !== 'All'; });
    var careAgentValues = careAgentOptions.filter(function (option) { return option !== 'All'; });
    var filteredCareAgentValues = careAgentValues.filter(function (agent) {
        return agent.toLowerCase().includes(careAgentSearch.trim().toLowerCase());
    });
    var isCallTypeAllSelected = !isCallTypeAllDeselected && callType.length === 0;
    var isCallDirectionAllSelected = !isCallDirectionAllDeselected && callDirection === 'All';
    var isCareAgentAllSelected = !isCareAgentAllDeselected && selectedCareAgents.length === 0;
    var hasAnyFilterValue = callType.length > 0 ||
        isCallTypeAllDeselected ||
        callDirection !== 'All' ||
        isCallDirectionAllDeselected ||
        selectedCareAgents.length > 0 ||
        isCareAgentAllDeselected;
    var updateFilters = function (updates) {
        onChange === null || onChange === void 0 ? void 0 : onChange(__assign({ callType: callType, callDirection: callDirection, startDate: appliedStartDate, endDate: appliedEndDate }, updates));
    };
    var handleCallTypeChange = function (value) {
        if (!value) {
            if (isCallTypeAllSelected) {
                setIsCallTypeAllDeselected(true);
                return;
            }
            setIsCallTypeAllDeselected(false);
            setCallType([]);
            updateFilters({ callType: [] });
            return;
        }
        var nextValues = isCallTypeAllDeselected
            ? [value]
            : isCallTypeAllSelected
                ? callTypeValues.filter(function (option) { return option !== value; })
                : callType.includes(value)
                    ? callType.filter(function (option) { return option !== value; })
                    : __spreadArray(__spreadArray([], callType, true), [value], false);
        if (!nextValues.length) {
            setCallType([]);
            setIsCallTypeAllDeselected(true);
            return;
        }
        var normalizedValues = nextValues.length === callTypeValues.length ? [] : nextValues;
        setCallType(normalizedValues);
        setIsCallTypeAllDeselected(false);
        updateFilters({ callType: normalizedValues });
    };
    var handleCallDirectionChange = function (value) {
        if (value === 'All') {
            if (isCallDirectionAllSelected) {
                setIsCallDirectionAllDeselected(true);
                return;
            }
            setCallDirection('All');
            setIsCallDirectionAllDeselected(false);
            updateFilters({ callDirection: 'All' });
            return;
        }
        if (isCallDirectionAllDeselected) {
            setCallDirection(value);
            setIsCallDirectionAllDeselected(false);
            updateFilters({ callDirection: value });
            return;
        }
        if (isCallDirectionAllSelected) {
            var remainingDirection = CALL_DIRECTION_OPTIONS.find(function (option) { return option !== 'All' && option !== value; });
            setCallDirection(remainingDirection || 'All');
            setIsCallDirectionAllDeselected(false);
            updateFilters({ callDirection: remainingDirection || 'All' });
            return;
        }
        if (callDirection === value) {
            setIsCallDirectionAllDeselected(true);
            return;
        }
        setCallDirection('All');
        setIsCallDirectionAllDeselected(false);
        updateFilters({ callDirection: 'All' });
    };
    var handleCareAgentChange = function (value) {
        if (!value) {
            if (isCareAgentAllSelected) {
                setIsCareAgentAllDeselected(true);
                setSelectedCareAgents([]);
                onCareAgentChange === null || onCareAgentChange === void 0 ? void 0 : onCareAgentChange([]);
                return;
            }
            setIsCareAgentAllDeselected(false);
            setSelectedCareAgents([]);
            onCareAgentChange === null || onCareAgentChange === void 0 ? void 0 : onCareAgentChange([]);
            return;
        }
        var nextValues = isCareAgentAllDeselected
            ? [value]
            : isCareAgentAllSelected
                ? careAgentValues.filter(function (agent) { return agent !== value; })
                : selectedCareAgents.includes(value)
                    ? selectedCareAgents.filter(function (agent) { return agent !== value; })
                    : __spreadArray(__spreadArray([], selectedCareAgents, true), [value], false);
        if (!nextValues.length) {
            setSelectedCareAgents([]);
            setIsCareAgentAllDeselected(true);
            onCareAgentChange === null || onCareAgentChange === void 0 ? void 0 : onCareAgentChange([]);
            return;
        }
        var normalizedValues = nextValues.length === careAgentValues.length ? [] : nextValues;
        setSelectedCareAgents(normalizedValues);
        setIsCareAgentAllDeselected(false);
        onCareAgentChange === null || onCareAgentChange === void 0 ? void 0 : onCareAgentChange(normalizedValues);
    };
    var handleStartDateChange = function (newValue) {
        setStartDate(newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '');
    };
    var handleEndDateChange = function (newValue) {
        setEndDate(newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '');
    };
    var handleApplyDates = function () {
        if (dateRangeInvalid)
            return;
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        onChange === null || onChange === void 0 ? void 0 : onChange({ callType: callType, callDirection: callDirection, startDate: startDate, endDate: endDate });
    };
    var handleClearDates = function () {
        setStartDate('');
        setEndDate('');
        setAppliedStartDate('');
        setAppliedEndDate('');
        onChange === null || onChange === void 0 ? void 0 : onChange({ callType: callType, callDirection: callDirection, startDate: '', endDate: '' });
    };
    var handleResetFilters = function () {
        setCallType([]);
        setIsCallTypeAllDeselected(false);
        setCallDirection(DEFAULT_FILTER_VALUE.callDirection);
        setIsCallDirectionAllDeselected(false);
        setSelectedCareAgents([]);
        setCareAgentSearch('');
        setIsCareAgentAllDeselected(false);
        onChange === null || onChange === void 0 ? void 0 : onChange(__assign(__assign({}, DEFAULT_FILTER_VALUE), { startDate: appliedStartDate, endDate: appliedEndDate }));
        onCareAgentChange === null || onCareAgentChange === void 0 ? void 0 : onCareAgentChange([]);
        setActiveFilter(null);
    };
    var closeFilterMenu = function () {
        setFilterAnchor(null);
        setActiveFilter(null);
    };
    return (_jsxs(Paper, { elevation: 0, className: classes.paper, "data-testid": "call-logs-filter", children: [_jsxs(Box, { className: classes.controlsRow, children: [_jsx(Button, { variant: "contained", className: classes.applyButton, endIcon: _jsx(KeyboardArrowDownIcon, {}), onClick: function (event) { return setFilterAnchor(event.currentTarget); }, disabled: disabled, "aria-haspopup": "true", "aria-expanded": Boolean(filterAnchor), "data-testid": "callLogsFilterMenu", children: "Select Filters" }), _jsx(Button, { variant: "outlined", className: classes.clearButton, onClick: handleResetFilters, disabled: disabled || !hasAnyFilterValue, "data-testid": "callLogsFilterReset", children: "Reset Filters" }), _jsx(Box, { className: classes.divider }), _jsx(LocalizationProvider, { dateAdapter: AdapterMoment, children: _jsxs(Box, { className: classes.dateGroup, children: [_jsx(DatePicker, { label: "Start Date", value: startDate ? moment(startDate) : null, onChange: handleStartDateChange, disabled: disabled, maxDate: endDate ? moment(endDate) : undefined, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { size: "small", className: classes.dateField, error: dateRangeInvalid, inputProps: __assign(__assign({}, params.inputProps), { readOnly: true, 'data-testid': 'callLogsStartDate' }), onKeyDown: function (e) { return e.preventDefault(); }, onPaste: function (e) { return e.preventDefault(); } }))); } }), _jsx(DatePicker, { label: "End Date", value: endDate ? moment(endDate) : null, onChange: handleEndDateChange, disabled: disabled, minDate: startDate ? moment(startDate) : undefined, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { size: "small", className: classes.dateField, error: dateRangeInvalid, inputProps: __assign(__assign({}, params.inputProps), { readOnly: true, 'data-testid': 'callLogsEndDate' }), onKeyDown: function (e) { return e.preventDefault(); }, onPaste: function (e) { return e.preventDefault(); } }))); } })] }) }), _jsxs(Menu, { anchorEl: filterAnchor, open: Boolean(filterAnchor), onClose: closeFilterMenu, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, MenuListProps: {
                            disablePadding: true,
                            sx: { maxHeight: 'min(360px, calc(100vh - 200px))', overflowY: 'auto' },
                        }, PaperProps: {
                            sx: { maxHeight: 'min(360px, calc(100vh - 200px))', overflowY: 'auto' },
                        }, "data-testid": "callLogsFilterDropdown", children: [_jsxs(MenuItem, { className: classes.menuItem, onClick: function () {
                                    return setActiveFilter(function (current) {
                                        return current === 'callType' ? null : 'callType';
                                    });
                                }, children: ["Call Type", activeFilter === 'callType' ? (_jsx(KeyboardArrowUpIcon, { fontSize: "small" })) : (_jsx(KeyboardArrowDownIcon, { fontSize: "small" }))] }), activeFilter === 'callType' && (_jsxs(Box, { className: classes.menuOptions, children: [_jsxs(MenuItem, { dense: true, onClick: function () { return handleCallTypeChange(); }, "data-testid": "callTypeAllFilter", children: [_jsx(Checkbox, { checked: isCallTypeAllSelected, tabIndex: -1, disableRipple: true, sx: { pointerEvents: 'none' } }), _jsx(ListItemText, { primary: "All Call Types" })] }), callTypeValues.map(function (option) { return (_jsxs(MenuItem, { dense: true, onClick: function () { return handleCallTypeChange(option); }, "data-testid": "callTypeOption-".concat(option), children: [_jsx(Checkbox, { checked: !isCallTypeAllDeselected &&
                                                    (callType.length === 0 || callType.includes(option)), tabIndex: -1, disableRipple: true, sx: { pointerEvents: 'none' } }), _jsx(ListItemText, { primary: option })] }, option)); })] })), _jsxs(MenuItem, { className: classes.menuItem, onClick: function () {
                                    return setActiveFilter(function (current) {
                                        return current === 'callDirection' ? null : 'callDirection';
                                    });
                                }, children: ["Call Direction", activeFilter === 'callDirection' ? (_jsx(KeyboardArrowUpIcon, { fontSize: "small" })) : (_jsx(KeyboardArrowDownIcon, { fontSize: "small" }))] }), activeFilter === 'callDirection' && (_jsxs(Box, { className: classes.menuOptions, children: [_jsxs(MenuItem, { dense: true, onClick: function () { return handleCallDirectionChange('All'); }, "data-testid": "callDirectionAllFilter", children: [_jsx(Checkbox, { checked: isCallDirectionAllSelected, tabIndex: -1, disableRipple: true, sx: { pointerEvents: 'none' } }), _jsx(ListItemText, { primary: "All Call Directions" })] }), CALL_DIRECTION_OPTIONS.filter(function (option) { return option !== 'All'; }).map(function (option) { return (_jsxs(MenuItem, { dense: true, onClick: function () { return handleCallDirectionChange(option); }, "data-testid": "callDirectionOption-".concat(option), children: [_jsx(Checkbox, { checked: !isCallDirectionAllDeselected &&
                                                    (callDirection === 'All' || callDirection === option), tabIndex: -1, disableRipple: true, sx: { pointerEvents: 'none' } }), _jsx(ListItemText, { primary: option })] }, option)); })] })), _jsxs(MenuItem, { className: classes.menuItem, onClick: function () {
                                    return setActiveFilter(function (current) {
                                        return current === 'agent' ? null : 'agent';
                                    });
                                }, children: ["Agent", activeFilter === 'agent' ? (_jsx(KeyboardArrowUpIcon, { fontSize: "small" })) : (_jsx(KeyboardArrowDownIcon, { fontSize: "small" }))] }), activeFilter === 'agent' && (_jsxs(Box, { className: classes.menuOptions, children: [_jsx(TextField, { size: "small", placeholder: "Search agents", value: careAgentSearch, onChange: function (event) { return setCareAgentSearch(event.target.value); }, inputProps: { 'data-testid': 'careAgentSearchInput' }, sx: { width: 280, px: 1, py: 0.5 } }), _jsxs(MenuItem, { dense: true, onClick: function () { return handleCareAgentChange(); }, "data-testid": "careAgentAllFilter", children: [_jsx(Checkbox, { checked: isCareAgentAllSelected, tabIndex: -1, disableRipple: true, sx: { pointerEvents: 'none' } }), _jsx(ListItemText, { primary: "All Agents" })] }), _jsxs(Box, { sx: { maxHeight: 200, overflowY: 'auto' }, children: [filteredCareAgentValues.map(function (option) { return (_jsxs(MenuItem, { dense: true, onClick: function () { return handleCareAgentChange(option); }, "data-testid": "careAgentOption-".concat(option), children: [_jsx(Checkbox, { checked: !isCareAgentAllDeselected &&
                                                            (selectedCareAgents.length === 0 ||
                                                                selectedCareAgents.includes(option)), tabIndex: -1, disableRipple: true, sx: { pointerEvents: 'none' } }), _jsx(ListItemText, { primary: option })] }, option)); }), !filteredCareAgentValues.length && (_jsx(MenuItem, { dense: true, disabled: true, children: _jsx(ListItemText, { primary: "No agents found" }) }))] })] }))] })] }), _jsxs(Box, { className: classes.actionsRow, children: [_jsx(Button, { variant: "outlined", className: classes.clearButton, onClick: handleClearDates, disabled: disabled || !hasAnyDateValue, "data-testid": "callLogsFilterClear", children: "Clear Dates" }), _jsx(Button, { variant: "contained", className: classes.applyButton, onClick: handleApplyDates, disabled: disabled || dateRangeInvalid || !hasPendingDateChange, "data-testid": "callLogsFilterApply", children: "Apply Dates" })] }), dateRangeInvalid && (_jsx(Typography, { className: classes.errorText, children: "End date must be after start date" }))] }));
};
export default SeniorCallLogsFilter;
